import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import trim from 'lodash/trim';
import { useMemo } from 'react';

import { Language, UseCurrentLangHook } from './models';

export const useCurrentLang: UseCurrentLangHook = (defaultLangPath = '/') => {
  const { pathname } = useLocation();

  const {
    allSitePage: { nodes },
  } = useStaticQuery(graphql`
    {
      allSitePage {
        nodes {
          context {
            uid
            locale
            parentUid
          }
          path
        }
      }
    }
  `);

  const languages = useMemo(
    (): Language[] =>
      nodes
        .filter(({ context: { uid, parentUid } }) => uid && uid === parentUid)
        .map(
          ({ context: { locale }, path }): Language => ({ locale, url: path })
        )
        .sort((a, b) => b.url.length - a.url.length),
    [nodes]
  );

  const defaultLang = useMemo(
    (): Language | undefined =>
      languages.find((language) => language.url === defaultLangPath),
    [defaultLangPath, languages]
  );

  if (!defaultLang) {
    throw new Error(`
      Problem, can't find the default language.
      Try using the parameter, defaultLang to help find main page.
    `);
  }

  const urlLang = pathname.split('/')[1];

  const searchForCurrentLang = useMemo(
    () => languages.find((language) => trim(language.url, '/') === urlLang),
    [languages, urlLang]
  );

  const currentLang = searchForCurrentLang || defaultLang;

  return { languages, currentLang, defaultLang };
};
